/**
 * Modal de configuración de columnas por cliente.
 * Spec: docs/architecture/COLUMN_CONFIG_UI_SPEC.md
 *
 * Tres secciones (Core / Native / Tags) con drag-drop y format presets para
 * Native/Tags. Llama a los endpoints de Slice 1+2.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Save,
  RefreshCw,
  Settings,
  Columns3,
  Loader2,
  AlertCircle,
  Trash2,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import ConfigSectionShell from '../common/ConfigSectionShell';
import {
  getColumnConfig,
  saveColumnConfig,
  scanNativeColumns,
  getDiscoverableTags,
  ColumnConfigDoc,
  NativeCandidate,
  TagCandidate,
  DEFAULT_FILTER_LAYOUT,
  type FilterLayoutConfig,
} from '../../services/columnConfig';
import ColumnConfigSection, { SectionEntry, SectionType } from './ColumnConfigSection';
import ColumnTemplatesEditor from './ColumnTemplatesEditor';
import { DEFAULT_EXPORT_ATTRS } from '../../services/columnExportAttrs';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  inline?: boolean;
}

// Reorg PT-#67: 3 grupos top-level. Registro (Core/Native/Tags — solo determina
// qué columnas están habilitadas); Templates (vistas fijas PUBLICACIONES/DATA EXPORT
// con on/off + orden); Filtros (switches "permitir filtrar por…" + layout de filtros).
type TopTab = 'registro' | 'templates' | 'filtros';
type RegistroSub = 'core' | 'native' | 'tags' | 'calculadas';

// PT-#70 #14: columnas CALCULADAS / inyectadas por el exportador (no vienen del
// columnConfig core/native/tags — las agrega el pipeline de export). Read-only:
// title/format/ancho salen de DEFAULT_EXPORT_ATTRS. Editables a futuro.
const CALCULATED_COLUMNS: { id: string; description: string }[] = [
  { id: 'semana', description: 'Semana ISO 8601 (AAAA-Www) derivada de la fecha de la toma.' },
  { id: 'toma', description: 'Identificador de la toma de datos que originó la fila.' },
  { id: 'id_pulpou', description: 'ID interno único del producto en Pulpou.' },
  { id: 'url', description: 'Link a la publicación original (hyperlink en el Excel).' },
];

// Doc completamente vacío para fallback antes del primer fetch.
const EMPTY_CONFIG: ColumnConfigDoc = {
  schemaVersion: 1,
  core: {},
  native: {},
  tags: {},
};

// Convierte un dict de entries del config doc a array para la sección.
function dictToEntries(
  dict: Record<string, any> | undefined,
  candidatesById: Map<string, unknown> = new Map()
): SectionEntry[] {
  if (!dict) return [];
  return Object.entries(dict).map(([id, cfg]) => ({
    id,
    ...cfg,
    sample: cfg.sample !== undefined ? cfg.sample : candidatesById.get(id),
  }));
}

const ColumnConfigModal: React.FC<Props> = ({ isOpen, onClose, clientId, clientName, inline = false }) => {
  const { t } = useTranslation();

  const [activeTop, setActiveTop] = useState<TopTab>('registro');
  const [activeRegistroSub, setActiveRegistroSub] = useState<RegistroSub>('core');
  const [originalConfig, setOriginalConfig] = useState<ColumnConfigDoc>(EMPTY_CONFIG);
  const [currentConfig, setCurrentConfig] = useState<ColumnConfigDoc>(EMPTY_CONFIG);
  const [nativeCandidates, setNativeCandidates] = useState<NativeCandidate[]>([]);
  const [tagCandidates, setTagCandidates] = useState<TagCandidate[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastTomaScanned, setLastTomaScanned] = useState<string | null>(null);

  // ----- Fetch inicial -----
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, scanRes, tagsRes] = await Promise.allSettled([
        getColumnConfig(clientId),
        scanNativeColumns(clientId),
        getDiscoverableTags(clientId),
      ]);

      if (configRes.status === 'fulfilled') {
        const doc = configRes.value.columnConfig || EMPTY_CONFIG;
        // Snapshot profundo para detectar dirty.
        const snap = JSON.parse(JSON.stringify(doc));
        setOriginalConfig(snap);
        setCurrentConfig(JSON.parse(JSON.stringify(doc)));
      } else {
        console.error('[ColumnConfigModal] error fetching config:', configRes.reason);
        setError(
          (configRes.reason && configRes.reason?.response?.data?.error) ||
            (configRes.reason as Error)?.message ||
            'Error cargando configuración'
        );
      }

      if (scanRes.status === 'fulfilled') {
        setNativeCandidates(scanRes.value.candidates || []);
        setLastTomaScanned(scanRes.value.lastTomaScanned || null);
      } else {
        console.warn('[ColumnConfigModal] scan-native failed:', scanRes.reason);
        setNativeCandidates([]);
        setLastTomaScanned(null);
      }

      if (tagsRes.status === 'fulfilled') {
        setTagCandidates(tagsRes.value.tags || []);
      } else {
        console.warn('[ColumnConfigModal] discoverable-tags failed:', tagsRes.reason);
        setTagCandidates([]);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen) {
      loadAll();
    }
  }, [isOpen, loadAll]);

  // ----- Refresh on-demand -----
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [scanRes, tagsRes] = await Promise.allSettled([
        scanNativeColumns(clientId),
        getDiscoverableTags(clientId),
      ]);
      if (scanRes.status === 'fulfilled') setNativeCandidates(scanRes.value.candidates || []);
      if (tagsRes.status === 'fulfilled') setTagCandidates(tagsRes.value.tags || []);
    } finally {
      setRefreshing(false);
    }
  };

  // ----- Dirty detection -----
  const isDirty = useMemo(() => {
    return JSON.stringify(currentConfig) !== JSON.stringify(originalConfig);
  }, [currentConfig, originalConfig]);

  // ----- Mutators del config -----
  const updateEntry = (
    section: SectionType,
    id: string,
    updates: Partial<SectionEntry>
  ) => {
    setCurrentConfig((prev) => {
      const next = { ...prev };
      const sec = section === 'core' ? 'core' : section === 'native' ? 'native' : 'tags';
      const dict = { ...((next as any)[sec] || {}) };
      const existing = dict[id] || {};
      // sample no se persiste; se ignora al merge
      const { sample, ...persisted } = updates as any;
      dict[id] = { ...existing, ...persisted };
      (next as any)[sec] = dict;
      return next;
    });
  };

  const reorderSection = (section: SectionType, orderedIds: string[]) => {
    setCurrentConfig((prev) => {
      const next = { ...prev };
      const sec = section === 'core' ? 'core' : section === 'native' ? 'native' : 'tags';
      const dict = { ...((next as any)[sec] || {}) };
      orderedIds.forEach((id, idx) => {
        if (dict[id]) dict[id] = { ...dict[id], order: idx };
      });
      (next as any)[sec] = dict;
      return next;
    });
  };

  const addEntry = (
    section: SectionType,
    id: string,
    defaultEntry: Partial<SectionEntry>
  ) => {
    setCurrentConfig((prev) => {
      const next = { ...prev };
      const sec = section === 'core' ? 'core' : section === 'native' ? 'native' : 'tags';
      const dict = { ...((next as any)[sec] || {}) };
      if (dict[id]) return prev; // ya existe
      const { sample, ...persisted } = defaultEntry as any;
      dict[id] = persisted;
      (next as any)[sec] = dict;
      return next;
    });
  };

  const removeEntry = (section: SectionType, id: string) => {
    setCurrentConfig((prev) => {
      const next = { ...prev };
      const sec = section === 'core' ? 'core' : section === 'native' ? 'native' : 'tags';
      const dict = { ...((next as any)[sec] || {}) };
      delete dict[id];
      (next as any)[sec] = dict;
      return next;
    });
  };

  // ----- Save / Discard -----
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await saveColumnConfig(clientId, currentConfig);
      const fresh = res.columnConfig || currentConfig;
      const snap = JSON.parse(JSON.stringify(fresh));
      setOriginalConfig(snap);
      setCurrentConfig(JSON.parse(JSON.stringify(fresh)));
      // Cerrar tras guardar (preferencia explícita del task).
      onClose();
    } catch (e: any) {
      console.error('[ColumnConfigModal] save error:', e);
      setError(
        e?.response?.data?.error || e?.message || t('admin.columnConfig.toasts.saveError', 'Error al guardar')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isDirty) {
      const ok = window.confirm(
        t(
          'admin.columnConfig.warnings.unsavedChanges',
          'Tenés cambios sin guardar. ¿Descartar y salir?'
        )
      );
      if (!ok) return;
    }
    onClose();
  };

  // Aviso si el usuario recarga / cierra el browser con cambios pendientes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ----- Cálculo de candidatos sample para Native (para preview en filas existentes) -----
  const nativeSampleById = useMemo(() => {
    const m = new Map<string, unknown>();
    nativeCandidates.forEach((c) => m.set(c.id, c.sample));
    return m;
  }, [nativeCandidates]);

  const tagSampleByKey = useMemo(() => {
    const m = new Map<string, unknown>();
    tagCandidates.forEach((c) => m.set(c.tagKey, c.sampleValue));
    return m;
  }, [tagCandidates]);

  const coreEntries = useMemo(
    // Excluir 'subcategory' del listado: no es una columna Core renderable, sino
    // un slot de config (filterable flag). Su toggle se renderiza arriba del listado.
    () => dictToEntries(currentConfig.core).filter(e => e.id !== 'subcategory'),
    [currentConfig.core]
  );
  const nativeEntries = useMemo(
    () => dictToEntries(currentConfig.native, nativeSampleById),
    [currentConfig.native, nativeSampleById]
  );
  const tagEntries = useMemo(
    () => dictToEntries(currentConfig.tags, tagSampleByKey),
    [currentConfig.tags, tagSampleByKey]
  );

  if (!isOpen) return null;

  // Acciones del header (Refrescar / Descartar / Guardar). En inline van en headerRight
  // del shell; en modal van en el header ad-hoc (+ botón Cerrar).
  const headerActions = (
    <>
      <button
        onClick={handleRefresh}
        disabled={refreshing || loading}
        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
        title={t(
          'admin.columnConfig.actions.refreshTooltip',
          'Vuelve a escanear la base del cliente para detectar nuevas columnas Native (de la última toma) y nuevos tags. No descarta cambios pendientes.'
        )}
      >
        {refreshing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        {t('admin.columnConfig.actions.refresh', 'Refrescar')}
      </button>
      <button
        onClick={handleDiscard}
        disabled={saving || (inline && !isDirty)}
        className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
        title={t('admin.columnConfig.actions.discardTooltip', 'Descartar cambios')}
      >
        <Trash2 className="w-3.5 h-3.5" />
        {t('admin.columnConfig.actions.discard', 'Descartar')}
      </button>
      <button
        onClick={handleSave}
        disabled={!isDirty || saving || loading}
        className="px-3 py-1.5 text-xs font-medium bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        title={t('admin.columnConfig.actions.saveTooltip', 'Guardar cambios')}
      >
        {saving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Save className="w-3.5 h-3.5" />
        )}
        {t('admin.columnConfig.actions.save', 'Guardar cambios')}
      </button>
    </>
  );

  // Cuerpo (tab bar rico + contenido + footer de estado). El tab bar tiene counts,
  // badge de pendientes y tab Preview a la derecha → no se puede expresar con el tab
  // list simple del shell; se mantiene como primer hijo del contenido.
  const body = (
    <>
      {/* Tabs top-level: Registro | Templates | Filtros */}
      <div className={`border-b ${inline ? 'mb-3' : 'px-6'} py-2 flex items-center gap-1`}>
          {([
            { id: 'registro' as TopTab, label: t('admin.columnConfig.tabs.registro', 'Registro') },
            { id: 'templates' as TopTab, label: t('admin.columnConfig.tabs.templates', 'Templates') },
            { id: 'filtros' as TopTab, label: t('admin.columnConfig.tabs.filtros', 'Filtros') },
          ]).map((top) => {
            // badge de pendientes solo relevante en Registro (Native/Tags con candidatos nuevos)
            const pendingBadge =
              top.id === 'registro' &&
              (nativeCandidates.filter((c) => !c.alreadyConfigured).length > 0 ||
                tagCandidates.filter((c) => !c.alreadyConfigured).length > 0);
            return (
              <button
                key={top.id}
                onClick={() => setActiveTop(top.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                  activeTop === top.id
                    ? 'bg-pink-100 text-pink-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {top.label}
                {pendingBadge && (
                  <span className="inline-block w-1.5 h-1.5 bg-orange-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs de Registro: Core | Native | Tags (con counts + badge de pendientes) */}
        {activeTop === 'registro' && (
          <div className={`${inline ? 'mb-4' : 'px-6'} pb-2 flex items-center gap-1`}>
            {(['core', 'native', 'tags', 'calculadas'] as RegistroSub[]).map((sub) => {
              const count =
                sub === 'core'
                  ? coreEntries.length
                  : sub === 'native'
                  ? nativeEntries.length
                  : sub === 'tags'
                  ? tagEntries.length
                  : CALCULATED_COLUMNS.length;
              const pendingNative =
                sub === 'native' &&
                nativeCandidates.filter((c) => !c.alreadyConfigured).length;
              const pendingTags =
                sub === 'tags' &&
                tagCandidates.filter((c) => !c.alreadyConfigured).length;
              const pendingBadge =
                (typeof pendingNative === 'number' && pendingNative > 0) ||
                (typeof pendingTags === 'number' && pendingTags > 0);
              return (
                <button
                  key={sub}
                  onClick={() => setActiveRegistroSub(sub)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${
                    activeRegistroSub === sub
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {t(`admin.columnConfig.tabs.${sub}`, sub === 'calculadas' ? 'Calculadas' : sub)}
                  <span className={activeRegistroSub === sub ? 'text-gray-300' : 'text-gray-500'}>({count})</span>
                  {pendingBadge && (
                    <span className="inline-block w-1.5 h-1.5 bg-orange-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div className={inline ? 'flex-1 py-4' : 'flex-1 overflow-y-auto px-6 py-4'}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">
                {t('admin.columnConfig.loading', 'Cargando configuración...')}
              </p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              {/* ── REGISTRO: solo determina qué columnas están habilitadas. El orden
                    acá es un seed para templates nuevos, no el orden final (PT-#67). ── */}
              {activeTop === 'registro' && activeRegistroSub !== 'calculadas' && (
                <p className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                  {t('admin.columnConfig.registro.intro',
                    'El Registro determina qué columnas están habilitadas para el cliente. El orden acá es solo un default inicial para los Templates nuevos — el orden final de cada pantalla se define en la pestaña Templates.')}
                </p>
              )}
              {activeTop === 'registro' && activeRegistroSub === 'core' && (
                <ColumnConfigSection
                  sectionType="core"
                  entries={coreEntries}
                  clientId={clientId}
                  onChange={(id, updates) => updateEntry('core', id, updates)}
                  onReorder={(ids) => reorderSection('core', ids)}
                />
              )}
              {activeTop === 'registro' && activeRegistroSub === 'native' && (
                <ColumnConfigSection
                  sectionType="native"
                  entries={nativeEntries}
                  candidates={nativeCandidates}
                  clientId={clientId}
                  onChange={(id, updates) => updateEntry('native', id, updates)}
                  onReorder={(ids) => reorderSection('native', ids)}
                  onAdd={(id, def) => addEntry('native', id, def)}
                  onRemove={(id) => removeEntry('native', id)}
                />
              )}
              {activeTop === 'registro' && activeRegistroSub === 'tags' && (
                <ColumnConfigSection
                  sectionType="tags"
                  entries={tagEntries}
                  candidates={tagCandidates}
                  clientId={clientId}
                  onChange={(id, updates) => updateEntry('tags', id, updates)}
                  onReorder={(ids) => reorderSection('tags', ids)}
                  onAdd={(id, def) => addEntry('tags', id, def)}
                  onRemove={(id) => removeEntry('tags', id)}
                />
              )}
              {/* ── CALCULADAS: columnas inyectadas por el exportador, read-only (PT-#70 #14) ── */}
              {activeTop === 'registro' && activeRegistroSub === 'calculadas' && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-2 mb-1">
                    {t('admin.columnConfig.calculadas.intro',
                      'Estas columnas no vienen del Registro: las agrega el exportador automáticamente. El título, formato y ancho salen de los defaults de exportación. Read-only por ahora (editables a futuro).')}
                  </p>
                  {CALCULATED_COLUMNS.map(({ id, description }) => {
                    const attrs = DEFAULT_EXPORT_ATTRS[id];
                    return (
                      <div
                        key={id}
                        className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-between gap-3"
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-gray-800">
                            {attrs?.title || id}
                          </span>
                          <span className="text-xs text-gray-500">{description}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[11px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            {id}
                          </span>
                          <span className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                            {attrs?.format || 'text'}
                          </span>
                          {typeof attrs?.widthChars === 'number' && (
                            <span className="text-[11px] text-gray-600 bg-white border border-gray-200 px-1.5 py-0.5 rounded tabular-nums">
                              {attrs.widthChars} ch
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* ── TEMPLATES: vistas fijas PUBLICACIONES / DATA EXPORT (PT-#67) ── */}
              {activeTop === 'templates' && (
                <ColumnTemplatesEditor
                  config={currentConfig}
                  clientId={clientId}
                  lastTomaScanned={lastTomaScanned}
                  onChange={(templates) =>
                    setCurrentConfig((prev) => ({ ...prev, templates }))
                  }
                />
              )}
              {/* ── FILTROS: switches "permitir filtrar por…" + layout de filtros ── */}
              {activeTop === 'filtros' && (
                <div className="mb-4 space-y-3">
                  {/* Toggle opt-in: Filtro de Subcategoría en UnifiedFilterPanel.
                      Almacenado en core.subcategory.filterable. Pedido Belen Silva / Samsung. */}
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-800">
                        {t('admin.columnConfig.subcategoryFilter.label', 'Permitir filtrar por subcategoría')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {t('admin.columnConfig.subcategoryFilter.help', 'Habilita el dropdown de subcategoría en el panel de filtros (requiere catalog.subcategory).')}
                      </span>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!(currentConfig.core?.subcategory as any)?.filterable}
                        onChange={(e) => updateEntry('core', 'subcategory', { filterable: e.target.checked } as any)}
                        className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                      />
                      <span className="text-xs text-gray-700">{t('common.enabled', 'Habilitado')}</span>
                    </label>
                  </div>
                  {/* Toggle opt-in: Filtro de Segmento en UnifiedFilterPanel.
                      Almacenado en core.segmento.filterable. */}
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-800">
                        {t('admin.columnConfig.segmentoFilter.label', 'Permitir filtrar por segmento')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {t('admin.columnConfig.segmentoFilter.help', 'Habilita el dropdown de segmento en el panel de filtros (requiere catalog.segmento).')}
                      </span>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!(currentConfig.core?.segmento as any)?.filterable}
                        onChange={(e) => updateEntry('core', 'segmento', { filterable: e.target.checked } as any)}
                        className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                      />
                      <span className="text-xs text-gray-700">{t('common.enabled', 'Habilitado')}</span>
                    </label>
                  </div>
                </div>
              )}
              {activeTop === 'filtros' && (() => {
                // FilterLayout editor — admin can configure filter rows/fields per client.
                const layout: FilterLayoutConfig =
                  (currentConfig as any).filterLayout || { rows: [] };

                const ALL_FILTER_KEYS = ['brand', 'site', 'category', 'subcategory', 'segment'] as const;
                const FILTER_LABELS: Record<string, string> = {
                  brand: t('columns.marca'),
                  site: t('columns.sitio'),
                  category: t('columns.categoria'),
                  subcategory: t('columns.subcategoria', 'Subcategoría'),
                  segment: t('columns.segmento', 'Segmento'),
                };
                const WIDTH_OPTIONS = [25, 33, 50] as const;

                // Collect all fields that are already placed in a row.
                const placedKeys = new Set(layout.rows.flatMap(r => r.fields.map(f => f.key)));

                const updateLayout = (newLayout: FilterLayoutConfig) => {
                  setCurrentConfig((prev: any) => ({ ...prev, filterLayout: newLayout }));
                };

                const setFieldProp = (rowIdx: number, fieldIdx: number, prop: string, value: any) => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  rows[rowIdx].fields[fieldIdx][prop] = value;
                  updateLayout({ rows });
                };

                const moveFieldUp = (rowIdx: number, fieldIdx: number) => {
                  if (fieldIdx === 0) return;
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  const tmp = rows[rowIdx].fields[fieldIdx - 1];
                  rows[rowIdx].fields[fieldIdx - 1] = rows[rowIdx].fields[fieldIdx];
                  rows[rowIdx].fields[fieldIdx] = tmp;
                  updateLayout({ rows });
                };

                const moveFieldDown = (rowIdx: number, fieldIdx: number) => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  if (fieldIdx >= rows[rowIdx].fields.length - 1) return;
                  const tmp = rows[rowIdx].fields[fieldIdx + 1];
                  rows[rowIdx].fields[fieldIdx + 1] = rows[rowIdx].fields[fieldIdx];
                  rows[rowIdx].fields[fieldIdx] = tmp;
                  updateLayout({ rows });
                };

                const moveFieldToRow = (fromRow: number, fieldIdx: number, toRow: number) => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  const [field] = rows[fromRow].fields.splice(fieldIdx, 1);
                  // Ensure target row exists.
                  while (rows.length <= toRow) rows.push({ fields: [] });
                  rows[toRow].fields.push(field);
                  // Remove empty rows.
                  const cleaned = rows.filter((r: any) => r.fields.length > 0);
                  updateLayout({ rows: cleaned });
                };

                const removeField = (rowIdx: number, fieldIdx: number) => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  rows[rowIdx].fields.splice(fieldIdx, 1);
                  const cleaned = rows.filter((r: any) => r.fields.length > 0);
                  updateLayout({ rows: cleaned });
                };

                const addField = (key: string) => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  // Add to last row, or create row 1 if empty.
                  if (rows.length === 0) rows.push({ fields: [] });
                  rows[rows.length - 1].fields.push({ key, enabled: true, widthPct: 25 });
                  updateLayout({ rows });
                };

                const addRow = () => {
                  const rows = JSON.parse(JSON.stringify(layout.rows));
                  rows.push({ fields: [] });
                  updateLayout({ rows });
                };

                const applyDefault = () => {
                  updateLayout(JSON.parse(JSON.stringify(DEFAULT_FILTER_LAYOUT)));
                };

                const clearLayout = () => {
                  updateLayout({ rows: [] });
                };

                return (
                  <div>
                    <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                      {t('admin.columnConfig.filterLayout.intro',
                        'Configura el orden y distribución de los dropdowns de filtro. Si no hay config, se usa el layout por defecto (grilla plana).')}
                    </p>
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={applyDefault}
                        className="px-3 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
                      >
                        {t('admin.columnConfig.filterLayout.applyDefault', 'Aplicar layout por defecto')}
                      </button>
                      <button
                        onClick={clearLayout}
                        className="px-3 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100"
                      >
                        {t('admin.columnConfig.filterLayout.clear', 'Sin layout (grilla plana)')}
                      </button>
                    </div>

                    {layout.rows.length === 0 ? (
                      <div className="text-sm text-gray-500 italic mb-4">
                        {t('admin.columnConfig.filterLayout.noLayout', 'Sin layout configurado — se usa la grilla plana legacy.')}
                      </div>
                    ) : (
                      <div className="space-y-4 mb-4">
                        {layout.rows.map((row, rowIdx) => (
                          <div key={rowIdx} className="border border-gray-200 rounded-lg p-3 bg-white">
                            <div className="text-xs font-medium text-gray-500 mb-2">
                              {t('admin.columnConfig.filterLayout.row', 'Fila')} {rowIdx + 1}
                            </div>
                            {row.fields.length === 0 ? (
                              <div className="text-xs text-gray-400 italic">
                                {t('admin.columnConfig.filterLayout.emptyRow', 'Fila vacía — arrastrá filtros acá o eliminala.')}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {row.fields.map((field, fieldIdx) => (
                                  <div key={field.key} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded border border-gray-100">
                                    {/* Reorder arrows */}
                                    <div className="flex flex-col gap-0.5">
                                      <button
                                        onClick={() => moveFieldUp(rowIdx, fieldIdx)}
                                        disabled={fieldIdx === 0}
                                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                      >
                                        <ArrowUp className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => moveFieldDown(rowIdx, fieldIdx)}
                                        disabled={fieldIdx >= row.fields.length - 1}
                                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                      >
                                        <ArrowDown className="w-3 h-3" />
                                      </button>
                                    </div>
                                    {/* Label */}
                                    <span className="text-sm font-medium text-gray-700 flex-1 min-w-0">
                                      {FILTER_LABELS[field.key] || field.key}
                                    </span>
                                    {/* Enable toggle */}
                                    <label className="inline-flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={field.enabled}
                                        onChange={(e) => setFieldProp(rowIdx, fieldIdx, 'enabled', e.target.checked)}
                                        className="h-3.5 w-3.5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                                      />
                                      <span className="text-xs text-gray-500">{t('common.enabled', 'Habilitado')}</span>
                                    </label>
                                    {/* Width select */}
                                    <select
                                      value={field.widthPct}
                                      onChange={(e) => setFieldProp(rowIdx, fieldIdx, 'widthPct', Number(e.target.value))}
                                      className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white"
                                    >
                                      {WIDTH_OPTIONS.map(w => (
                                        <option key={w} value={w}>{w}%</option>
                                      ))}
                                    </select>
                                    {/* Move to row */}
                                    <select
                                      value={rowIdx}
                                      onChange={(e) => moveFieldToRow(rowIdx, fieldIdx, Number(e.target.value))}
                                      className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white"
                                    >
                                      {layout.rows.map((_, ri) => (
                                        <option key={ri} value={ri}>
                                          {t('admin.columnConfig.filterLayout.row', 'Fila')} {ri + 1}
                                        </option>
                                      ))}
                                    </select>
                                    {/* Remove */}
                                    <button
                                      onClick={() => removeField(rowIdx, fieldIdx)}
                                      className="text-gray-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add field / add row buttons */}
                    <div className="flex gap-2 flex-wrap">
                      {ALL_FILTER_KEYS.filter(k => !placedKeys.has(k)).map(k => (
                        <button
                          key={k}
                          onClick={() => addField(k)}
                          className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                        >
                          + {FILTER_LABELS[k] || k}
                        </button>
                      ))}
                      {layout.rows.length > 0 && (
                        <button
                          onClick={addRow}
                          className="px-2 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100"
                        >
                          + {t('admin.columnConfig.filterLayout.addRow', 'Nueva fila')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Footer — estado dirty. En inline las acciones ya viven en el header del shell,
            así que sólo se muestra el indicador; en modal se conservan Descartar/Guardar. */}
        <div className={`border-t flex items-center justify-between ${inline ? 'pt-3 mt-2' : 'px-6 py-3 bg-gray-50'}`}>
          <span className="text-xs text-gray-600">
            {isDirty ? (
              <span className="text-orange-700">
                {t('admin.columnConfig.dirty', 'Hay cambios sin guardar')}
              </span>
            ) : (
              <span>{t('admin.columnConfig.clean', 'Sin cambios')}</span>
            )}
          </span>
          {!inline && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleDiscard}
                disabled={saving}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded disabled:opacity-50"
              >
                {t('admin.columnConfig.actions.discard', 'Descartar')}
              </button>
              <button
                onClick={handleSave}
                disabled={!isDirty || saving || loading}
                className="px-4 py-1.5 text-sm font-medium bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t('admin.columnConfig.actions.saving', 'Guardando...')}
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    {t('admin.columnConfig.actions.save', 'Guardar cambios')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
    </>
  );

  // Inline: carcasa unificada (icon+title = sidebar: Columns3 / "Configurar Columnas")
  // con las acciones globales en headerRight. El tab bar rico va dentro del contenido.
  if (inline) {
    return (
      <ConfigSectionShell
        icon={Columns3}
        title="Configurar Columnas"
        clientName={clientName}
        headerRight={headerActions}
      >
        {body}
      </ConfigSectionShell>
    );
  }

  // Modal: card ad-hoc con header propio (uso desde ClientCard).
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Settings className="w-5 h-5 text-gray-700 flex-shrink-0" />
            <h2 className="text-lg font-semibold truncate">
              {t('admin.columnConfig.title', 'Configuración de Columnas')}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {headerActions}
            <button
              onClick={handleDiscard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.close', 'Cerrar')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {body}
      </div>
    </div>
  );
};

export default ColumnConfigModal;
